import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Mail, Phone, Inbox, Briefcase, Users, Download, Plus, Trash2, Pencil, X, Search, ArrowDownUp, Send, Star, FileText } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import RichTextEditor from "@/components/RichTextEditor";
import {
  fetchMe, getAdminContacts, getAdminApplications, getAdminStats, clearToken, getToken,
  updateContactStatus, updateApplicationStatus, downloadExport,
  adminListCaseStudies, adminCreateCaseStudy, adminUpdateCaseStudy, adminDeleteCaseStudy,
  adminListTestimonials, adminCreateTestimonial, adminUpdateTestimonial, adminDeleteTestimonial,
  adminListBlog, adminCreateBlog, adminUpdateBlog, adminDeleteBlog, sendDigest,
} from "@/lib/api";
import { LOGO_MARK } from "@/lib/constants";

const STATUSES = ["New", "In Progress", "Closed"];
const FILTERS = ["All", "New", "In Progress", "Closed"];
const SERVICE_OPTS = ["Website Development", "Software Development", "Gen AI Trainings", "Digital Marketing"];
const STATUS_STYLE = {
  New: "text-[color:var(--accent)] border-[color:var(--accent)]/40",
  "In Progress": "text-amber-400 border-amber-400/40",
  Closed: "text-white/40 border-white/20",
};

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

function StatusSelect({ value, onChange, testid }) {
  return (
    <Select value={value || "New"} onValueChange={onChange}>
      <SelectTrigger className={`rounded-none h-9 w-[150px] bg-transparent font-mono text-[11px] tracking-[0.15em] uppercase ${STATUS_STYLE[value] || STATUS_STYLE.New}`} data-testid={testid}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="rounded-none bg-[color:var(--surface)] border-white/15 font-mono">
        {STATUSES.map((s) => (
          <SelectItem key={s} value={s} className="rounded-none focus:bg-white/10 text-xs uppercase tracking-[0.15em]">{s}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function FilterBar({ value, onChange, counts }) {
  return (
    <div className="flex flex-wrap gap-2 mb-6" data-testid="status-filter">
      {FILTERS.map((f) => (
        <button
          key={f}
          onClick={() => onChange(f)}
          className={`font-mono text-[11px] tracking-[0.15em] uppercase px-4 py-2 border transition-colors ${value === f ? "bg-white text-black border-white" : "border-white/15 text-white/60 hover:border-white/40"}`}
          data-testid={`filter-${f.toLowerCase().replace(" ", "-")}`}
        >
          {f} {counts[f] != null ? `(${counts[f]})` : ""}
        </button>
      ))}
    </div>
  );
}

const inputCls = "w-full bg-transparent border-b border-white/20 focus:border-white focus:outline-none placeholder-white/30 font-mono py-2 text-white text-sm";
const labelCls = "font-mono text-[10px] tracking-[0.2em] uppercase text-white/40 mb-2 block";

const emptyCS = { slug: "", client: "", title: "", service: "Website Development", year: "", summary: "", before: "", after: "", challenge: "", solution: "", results: "", gallery: "", featured: true, m0v: "", m0l: "", m1v: "", m1l: "", m2v: "", m2l: "" };

function csToForm(cs) {
  const m = cs.metrics || [];
  return {
    ...emptyCS, ...cs,
    featured: cs.featured !== false,
    gallery: (cs.gallery || []).join("\n"),
    m0v: m[0]?.v || "", m0l: m[0]?.l || "",
    m1v: m[1]?.v || "", m1l: m[1]?.l || "",
    m2v: m[2]?.v || "", m2l: m[2]?.l || "",
  };
}
function formToCS(f) {
  const metrics = [[f.m0v, f.m0l], [f.m1v, f.m1l], [f.m2v, f.m2l]]
    .filter(([v, l]) => v && l).map(([v, l]) => ({ v, l }));
  return {
    slug: f.slug, client: f.client, title: f.title, service: f.service, year: f.year,
    summary: f.summary, before: f.before, after: f.after,
    challenge: f.challenge, solution: f.solution, results: f.results,
    gallery: f.gallery.split("\n").map((s) => s.trim()).filter(Boolean),
    metrics, featured: f.featured !== false,
  };
}

const emptyBlog = { slug: "", title: "", excerpt: "", category: "", author: "LIS Editorial", date: "", read: "4 min", cover: "", body: "" };

export default function Admin() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [applications, setApplications] = useState([]);
  const [cFilter, setCFilter] = useState("All");
  const [aFilter, setAFilter] = useState("All");
  // content
  const [caseStudies, setCaseStudies] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [csForm, setCsForm] = useState(null); // null=closed, object=editing/new
  const [csEditId, setCsEditId] = useState(null);
  const [tForm, setTForm] = useState(null);
  const [tEditId, setTEditId] = useState(null);
  const [blog, setBlog] = useState([]);
  const [blogForm, setBlogForm] = useState(null);
  const [blogEditId, setBlogEditId] = useState(null);
  const [cSearch, setCSearch] = useState("");
  const [aSearch, setASearch] = useState("");
  const [sortOldest, setSortOldest] = useState(false);
  const [digestBusy, setDigestBusy] = useState(false);

  const loadContent = () => {
    adminListCaseStudies().then(setCaseStudies).catch(() => {});
    adminListTestimonials().then(setTestimonials).catch(() => {});
    adminListBlog().then(setBlog).catch(() => {});
  };

  useEffect(() => {
    if (!getToken()) { navigate("/admin/login"); return; }
    fetchMe()
      .then(() => {
        setReady(true);
        getAdminStats().then(setStats).catch(() => {});
        getAdminContacts().then(setContacts).catch(() => {});
        getAdminApplications().then(setApplications).catch(() => {});
        loadContent();
      })
      .catch(() => { clearToken(); navigate("/admin/login"); });
  }, [navigate]);

  const logout = () => { clearToken(); navigate("/admin/login"); };

  const changeContactStatus = async (id, status) => {
    setContacts((p) => p.map((c) => (c.id === id ? { ...c, status } : c)));
    try { await updateContactStatus(id, status); toast.success(`Marked as ${status}`); }
    catch { toast.error("Could not update status"); }
  };
  const changeAppStatus = async (id, status) => {
    setApplications((p) => p.map((a) => (a.id === id ? { ...a, status } : a)));
    try { await updateApplicationStatus(id, status); toast.success(`Marked as ${status}`); }
    catch { toast.error("Could not update status"); }
  };

  const exportCsv = async (kind) => {
    try { await downloadExport(kind); toast.success("Export downloaded"); }
    catch { toast.error("Export failed"); }
  };

  const countBy = (list) => list.reduce((acc, x) => {
    const s = x.status || "New"; acc[s] = (acc[s] || 0) + 1; acc.All = (acc.All || 0) + 1; return acc;
  }, {});

  const filtered = (list, f) => (f === "All" ? list : list.filter((x) => (x.status || "New") === f));

  const applyView = (list, f, search) => {
    let out = filtered(list, f);
    if (search.trim()) {
      const q = search.toLowerCase();
      out = out.filter((x) =>
        [x.name, x.email, x.message, x.service, x.role, x.phone, x.company]
          .filter(Boolean).some((v) => v.toLowerCase().includes(q))
      );
    }
    out = [...out].sort((a, b) => {
      const cmp = (a.created_at || "").localeCompare(b.created_at || "");
      return sortOldest ? cmp : -cmp;
    });
    return out;
  };

  const doDigest = async () => {
    setDigestBusy(true);
    try {
      const r = await sendDigest();
      if (r.contacts === 0 && r.applications === 0) toast("No new leads in the last 24h to send.");
      else toast.success(`Digest emailed — ${r.contacts} enquiries, ${r.applications} applications`);
    } catch { toast.error("Could not send digest"); }
    finally { setDigestBusy(false); }
  };

  // ---- Case study save ----
  const saveCS = async (e) => {
    e.preventDefault();
    const payload = formToCS(csForm);
    if (!payload.slug || !payload.title || !payload.client) { toast.error("Slug, title and client are required"); return; }
    try {
      if (csEditId) { await adminUpdateCaseStudy(csEditId, payload); toast.success("Case study updated"); }
      else { await adminCreateCaseStudy(payload); toast.success("Case study created"); }
      setCsForm(null); setCsEditId(null); loadContent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };
  const delCS = async (id) => {
    if (!window.confirm("Delete this case study?")) return;
    try { await adminDeleteCaseStudy(id); toast.success("Deleted"); loadContent(); } catch { toast.error("Delete failed"); }
  };

  // ---- Testimonial save ----
  const saveT = async (e) => {
    e.preventDefault();
    if (!tForm.quote || !tForm.name) { toast.error("Quote and name are required"); return; }
    try {
      if (tEditId) { await adminUpdateTestimonial(tEditId, tForm); toast.success("Testimonial updated"); }
      else { await adminCreateTestimonial(tForm); toast.success("Testimonial created"); }
      setTForm(null); setTEditId(null); loadContent();
    } catch { toast.error("Save failed"); }
  };
  const delT = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try { await adminDeleteTestimonial(id); toast.success("Deleted"); loadContent(); } catch { toast.error("Delete failed"); }
  };

  // ---- Blog save ----
  const saveBlog = async (e) => {
    e.preventDefault();
    if (!blogForm.slug || !blogForm.title) { toast.error("Slug and title are required"); return; }
    try {
      if (blogEditId) { await adminUpdateBlog(blogEditId, blogForm); toast.success("Post updated"); }
      else { await adminCreateBlog(blogForm); toast.success("Post published"); }
      setBlogForm(null); setBlogEditId(null); loadContent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };
  const delBlog = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try { await adminDeleteBlog(id); toast.success("Deleted"); loadContent(); } catch { toast.error("Delete failed"); }
  };
  const toggleFeatured = async (kind, item) => {
    const next = item.featured === false;
    try {
      if (kind === "cs") await adminUpdateCaseStudy(item.id, { ...formToCS(csToForm(item)), featured: next });
      else await adminUpdateTestimonial(item.id, { quote: item.quote, name: item.name, role: item.role, featured: next });
      toast.success(next ? "Shown on homepage" : "Hidden from homepage");
      loadContent();
    } catch { toast.error("Update failed"); }
  };

  if (!ready) return <div className="min-h-screen bg-[color:var(--bg)] flex items-center justify-center font-mono text-white/40">Authenticating…</div>;

  const statCards = [
    { icon: Inbox, label: "Enquiries", value: stats?.contacts ?? "—" },
    { icon: Briefcase, label: "Applications", value: stats?.applications ?? "—" },
    { icon: Users, label: "Subscribers", value: stats?.newsletter ?? "—" },
  ];

  return (
    <div className="min-h-screen bg-[color:var(--bg)]" data-testid="admin-dashboard">
      <div className="grain" aria-hidden="true" />
      <header className="border-b border-white/10 sticky top-0 bg-black/90 backdrop-blur-2xl z-50">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_MARK} alt="LIS" className="logo-invert h-9 w-auto" />
            <span className="font-mono text-xs tracking-[0.2em] uppercase text-white/40 hidden sm:block">Admin Console</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={doDigest} disabled={digestBusy} className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase border border-white/20 px-5 py-3 hover:bg-white hover:text-black transition-colors disabled:opacity-50" data-testid="send-digest">
              <Send size={14} /> {digestBusy ? "Sending…" : "Email digest"}
            </button>
            <button onClick={logout} className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase border border-white/20 px-5 py-3 hover:bg-white hover:text-black transition-colors" data-testid="admin-logout">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-12 md:py-16">
        <h1 className="font-display text-5xl md:text-7xl uppercase tracking-tighter leading-[0.85] mb-12">Dashboard</h1>

        <div className="grid grid-cols-1 sm:grid-cols-3 border-l border-t border-white/10 mb-14">
          {statCards.map((s) => (
            <div key={s.label} className="border-r border-b border-white/10 p-8 flex items-center justify-between" data-testid={`stat-${s.label.toLowerCase()}`}>
              <div>
                <div className="font-display text-5xl tracking-tighter">{s.value}</div>
                <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-white/45 mt-2">{s.label}</div>
              </div>
              <s.icon size={28} className="text-white/20" />
            </div>
          ))}
        </div>

        <Tabs defaultValue="contacts" className="w-full">
          <TabsList className="bg-[color:var(--surface)] border border-white/10 rounded-none p-1 mb-8 flex-wrap h-auto">
            <TabsTrigger value="contacts" className="rounded-none font-mono text-xs tracking-[0.15em] uppercase data-[state=active]:bg-white data-[state=active]:text-black" data-testid="tab-contacts">Enquiries ({contacts.length})</TabsTrigger>
            <TabsTrigger value="applications" className="rounded-none font-mono text-xs tracking-[0.15em] uppercase data-[state=active]:bg-white data-[state=active]:text-black" data-testid="tab-applications">Applications ({applications.length})</TabsTrigger>
            <TabsTrigger value="content" className="rounded-none font-mono text-xs tracking-[0.15em] uppercase data-[state=active]:bg-white data-[state=active]:text-black" data-testid="tab-content">Content</TabsTrigger>
          </TabsList>

          {/* ENQUIRIES */}
          <TabsContent value="contacts">
            <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
              <FilterBar value={cFilter} onChange={setCFilter} counts={countBy(contacts)} />
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2 border-b border-white/20 focus-within:border-white transition-colors">
                  <Search size={14} className="text-white/40" />
                  <input value={cSearch} onChange={(e) => setCSearch(e.target.value)} placeholder="Search…" className="bg-transparent py-2 w-40 font-mono text-xs text-white placeholder-white/30 focus:outline-none" data-testid="search-contacts" />
                </div>
                <button onClick={() => setSortOldest((v) => !v)} className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.15em] uppercase border border-white/20 px-4 py-2.5 hover:bg-white hover:text-black transition-colors" data-testid="sort-contacts">
                  <ArrowDownUp size={13} /> {sortOldest ? "Oldest" : "Newest"}
                </button>
                <button onClick={() => exportCsv("contacts")} className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.15em] uppercase border border-white/20 px-5 py-2.5 hover:bg-white hover:text-black transition-colors" data-testid="export-contacts">
                  <Download size={14} /> Export CSV
                </button>
              </div>
            </div>
            {applyView(contacts, cFilter, cSearch).length === 0 ? (
              <p className="font-mono text-sm text-white/40 py-10">No enquiries here.</p>
            ) : (
              <div className="space-y-4">
                {applyView(contacts, cFilter, cSearch).map((c) => (
                  <div key={c.id} className="border border-white/10 p-6 md:p-8 hover:border-white/25 transition-colors" data-testid="contact-row">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
                      <div className="flex items-center gap-4">
                        <h3 className="font-display text-2xl uppercase tracking-tight">{c.name}</h3>
                        {c.company && <span className="font-mono text-[10px] tracking-[0.15em] uppercase border border-white/15 px-3 py-1 text-white/70">{c.company}</span>}
                        {c.service && <span className="font-mono text-[10px] tracking-[0.15em] uppercase border border-white/15 px-3 py-1 text-[color:var(--accent)]">{c.service}</span>}
                      </div>
                      <span className="font-mono text-xs text-white/40">{fmtDate(c.created_at)}</span>
                    </div>
                    <p className="font-mono text-sm text-white/65 mb-5 leading-relaxed">{c.message}</p>
                    <div className="flex flex-wrap items-center gap-6 font-mono text-xs text-white/50">
                      <a href={`mailto:${c.email}`} className="inline-flex items-center gap-2 hover:text-white transition-colors"><Mail size={14} className="text-[color:var(--accent)]" /> {c.email}</a>
                      {c.phone && <a href={`tel:${c.phone}`} className="inline-flex items-center gap-2 hover:text-white transition-colors"><Phone size={14} className="text-[color:var(--accent)]" /> {c.phone}</a>}
                      <div className="ml-auto"><StatusSelect value={c.status} onChange={(s) => changeContactStatus(c.id, s)} testid={`contact-status-${c.id}`} /></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* APPLICATIONS */}
          <TabsContent value="applications">
            <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
              <FilterBar value={aFilter} onChange={setAFilter} counts={countBy(applications)} />
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2 border-b border-white/20 focus-within:border-white transition-colors">
                  <Search size={14} className="text-white/40" />
                  <input value={aSearch} onChange={(e) => setASearch(e.target.value)} placeholder="Search…" className="bg-transparent py-2 w-40 font-mono text-xs text-white placeholder-white/30 focus:outline-none" data-testid="search-applications" />
                </div>
                <button onClick={() => setSortOldest((v) => !v)} className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.15em] uppercase border border-white/20 px-4 py-2.5 hover:bg-white hover:text-black transition-colors" data-testid="sort-applications">
                  <ArrowDownUp size={13} /> {sortOldest ? "Oldest" : "Newest"}
                </button>
                <button onClick={() => exportCsv("applications")} className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.15em] uppercase border border-white/20 px-5 py-2.5 hover:bg-white hover:text-black transition-colors" data-testid="export-applications">
                  <Download size={14} /> Export CSV
                </button>
              </div>
            </div>
            {applyView(applications, aFilter, aSearch).length === 0 ? (
              <p className="font-mono text-sm text-white/40 py-10">No applications here.</p>
            ) : (
              <div className="space-y-4">
                {applyView(applications, aFilter, aSearch).map((a) => (
                  <div key={a.id} className="border border-white/10 p-6 md:p-8 hover:border-white/25 transition-colors" data-testid="application-row">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
                      <div className="flex items-center gap-4">
                        <h3 className="font-display text-2xl uppercase tracking-tight">{a.name}</h3>
                        <span className="font-mono text-[10px] tracking-[0.15em] uppercase border border-white/15 px-3 py-1 text-[color:var(--accent)]">{a.role}</span>
                      </div>
                      <span className="font-mono text-xs text-white/40">{fmtDate(a.created_at)}</span>
                    </div>
                    <p className="font-mono text-sm text-white/65 mb-5 leading-relaxed">{a.message}</p>
                    <div className="flex flex-wrap items-center gap-6 font-mono text-xs text-white/50">
                      <a href={`mailto:${a.email}`} className="inline-flex items-center gap-2 hover:text-white transition-colors"><Mail size={14} className="text-[color:var(--accent)]" /> {a.email}</a>
                      {a.phone && <a href={`tel:${a.phone}`} className="inline-flex items-center gap-2 hover:text-white transition-colors"><Phone size={14} className="text-[color:var(--accent)]" /> {a.phone}</a>}
                      {a.portfolio && <a href={a.portfolio} target="_blank" rel="noreferrer" className="hover:text-white transition-colors underline">Portfolio</a>}
                      <div className="ml-auto"><StatusSelect value={a.status} onChange={(s) => changeAppStatus(a.id, s)} testid={`application-status-${a.id}`} /></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* CONTENT */}
          <TabsContent value="content">
            {/* Case studies */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-3xl uppercase tracking-tight">Case Studies</h2>
              <button onClick={() => { setCsEditId(null); setCsForm({ ...emptyCS }); }} className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.15em] uppercase bg-white text-black px-5 py-2.5 hover:bg-[color:var(--accent)] hover:text-white transition-colors" data-testid="add-case-study">
                <Plus size={14} /> New case study
              </button>
            </div>
            <div className="space-y-3 mb-16">
              {caseStudies.map((cs) => (
                <div key={cs.id} className="border border-white/10 p-5 flex items-center justify-between gap-4" data-testid={`cs-row-${cs.slug}`}>
                  <div>
                    <div className="font-display text-xl uppercase tracking-tight">{cs.title}</div>
                    <div className="font-mono text-xs text-white/40 mt-1">{cs.client} · {cs.service} · /{cs.slug}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => toggleFeatured("cs", cs)} title="Show on homepage" className={`p-2 border transition-colors ${cs.featured !== false ? "border-[color:var(--accent)] text-[color:var(--accent)]" : "border-white/15 text-white/40 hover:border-white/40"}`} data-testid={`cs-feature-${cs.slug}`}><Star size={14} fill={cs.featured !== false ? "currentColor" : "none"} /></button>
                    <button onClick={() => { setCsEditId(cs.id); setCsForm(csToForm(cs)); }} className="p-2 border border-white/15 hover:bg-white hover:text-black transition-colors" data-testid={`cs-edit-${cs.slug}`}><Pencil size={14} /></button>
                    <button onClick={() => delCS(cs.id)} className="p-2 border border-white/15 hover:bg-[color:var(--accent)] hover:border-[color:var(--accent)] transition-colors" data-testid={`cs-delete-${cs.slug}`}><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>

            {/* Testimonials */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-3xl uppercase tracking-tight">Testimonials</h2>
              <button onClick={() => { setTEditId(null); setTForm({ quote: "", name: "", role: "" }); }} className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.15em] uppercase bg-white text-black px-5 py-2.5 hover:bg-[color:var(--accent)] hover:text-white transition-colors" data-testid="add-testimonial">
                <Plus size={14} /> New testimonial
              </button>
            </div>
            <div className="space-y-3">
              {testimonials.map((t) => (
                <div key={t.id} className="border border-white/10 p-5 flex items-center justify-between gap-4" data-testid={`t-row-${t.id}`}>
                  <div className="min-w-0">
                    <div className="font-mono text-sm text-white/70 truncate">"{t.quote}"</div>
                    <div className="font-mono text-xs text-white/40 mt-1">{t.name} — {t.role}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => toggleFeatured("t", t)} title="Show on homepage" className={`p-2 border transition-colors ${t.featured !== false ? "border-[color:var(--accent)] text-[color:var(--accent)]" : "border-white/15 text-white/40 hover:border-white/40"}`} data-testid={`t-feature-${t.id}`}><Star size={14} fill={t.featured !== false ? "currentColor" : "none"} /></button>
                    <button onClick={() => { setTEditId(t.id); setTForm({ quote: t.quote, name: t.name, role: t.role }); }} className="p-2 border border-white/15 hover:bg-white hover:text-black transition-colors" data-testid={`t-edit-${t.id}`}><Pencil size={14} /></button>
                    <button onClick={() => delT(t.id)} className="p-2 border border-white/15 hover:bg-[color:var(--accent)] hover:border-[color:var(--accent)] transition-colors" data-testid={`t-delete-${t.id}`}><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>

            {/* Blog */}
            <div className="flex items-center justify-between mb-6 mt-16">
              <h2 className="font-display text-3xl uppercase tracking-tight">Journal</h2>
              <button onClick={() => { setBlogEditId(null); setBlogForm({ ...emptyBlog }); }} className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.15em] uppercase bg-white text-black px-5 py-2.5 hover:bg-[color:var(--accent)] hover:text-white transition-colors" data-testid="add-blog">
                <Plus size={14} /> New post
              </button>
            </div>
            <div className="space-y-3">
              {blog.map((b) => (
                <div key={b.id} className="border border-white/10 p-5 flex items-center justify-between gap-4" data-testid={`blog-row-${b.slug}`}>
                  <div className="min-w-0 flex items-center gap-3">
                    <FileText size={16} className="text-white/30 shrink-0" />
                    <div className="min-w-0">
                      <div className="font-display text-xl uppercase tracking-tight truncate">{b.title}</div>
                      <div className="font-mono text-xs text-white/40 mt-1">{b.category} · {b.date} · /blog/{b.slug}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => { setBlogEditId(b.id); setBlogForm({ slug: b.slug, title: b.title, excerpt: b.excerpt || "", category: b.category || "", author: b.author || "LIS Editorial", date: b.date || "", read: b.read || "4 min", cover: b.cover || "", body: b.body || "" }); }} className="p-2 border border-white/15 hover:bg-white hover:text-black transition-colors" data-testid={`blog-edit-${b.slug}`}><Pencil size={14} /></button>
                    <button onClick={() => delBlog(b.id)} className="p-2 border border-white/15 hover:bg-[color:var(--accent)] hover:border-[color:var(--accent)] transition-colors" data-testid={`blog-delete-${b.slug}`}><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Case study drawer */}
      {csForm && (
        <div className="fixed inset-0 z-[130] flex justify-end" data-testid="cs-form">
          <div className="absolute inset-0 bg-black/70" onClick={() => setCsForm(null)} />
          <form onSubmit={saveCS} className="relative w-full max-w-2xl h-full bg-[color:var(--surface)] border-l border-white/10 overflow-y-auto p-8 md:p-10">
            <div className="flex items-center justify-between mb-8">
              <h3 className="font-display text-3xl uppercase tracking-tight">{csEditId ? "Edit" : "New"} case study</h3>
              <button type="button" onClick={() => setCsForm(null)} className="p-2 hover:text-[color:var(--accent)]"><X size={20} /></button>
            </div>
            <div className="grid grid-cols-2 gap-5">
              <div><label className={labelCls}>Slug *</label><input className={inputCls} value={csForm.slug} onChange={(e) => setCsForm({ ...csForm, slug: e.target.value })} placeholder="my-project" data-testid="cs-slug" /></div>
              <div><label className={labelCls}>Client *</label><input className={inputCls} value={csForm.client} onChange={(e) => setCsForm({ ...csForm, client: e.target.value })} /></div>
              <div className="col-span-2"><label className={labelCls}>Title *</label><input className={inputCls} value={csForm.title} onChange={(e) => setCsForm({ ...csForm, title: e.target.value })} data-testid="cs-title" /></div>
              <div>
                <label className={labelCls}>Service</label>
                <Select value={csForm.service} onValueChange={(v) => setCsForm({ ...csForm, service: v })}>
                  <SelectTrigger className="rounded-none border-0 border-b border-white/20 bg-transparent px-0 h-auto py-2 font-mono text-sm text-white focus:ring-0"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none bg-[color:var(--surface)] border-white/15 font-mono">
                    {SERVICE_OPTS.map((s) => <SelectItem key={s} value={s} className="rounded-none text-xs">{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><label className={labelCls}>Year</label><input className={inputCls} value={csForm.year} onChange={(e) => setCsForm({ ...csForm, year: e.target.value })} /></div>
              <div className="col-span-2"><label className={labelCls}>Summary</label><textarea rows={2} className={inputCls + " resize-none"} value={csForm.summary} onChange={(e) => setCsForm({ ...csForm, summary: e.target.value })} /></div>
              <div><label className={labelCls}>Before image URL</label><input className={inputCls} value={csForm.before} onChange={(e) => setCsForm({ ...csForm, before: e.target.value })} /></div>
              <div><label className={labelCls}>After image URL</label><input className={inputCls} value={csForm.after} onChange={(e) => setCsForm({ ...csForm, after: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-3 gap-4 mt-6">
              {[0, 1, 2].map((i) => (
                <div key={i} className="border border-white/10 p-3">
                  <label className={labelCls}>Metric {i + 1}</label>
                  <input className={inputCls + " mb-2"} placeholder="+220%" value={csForm[`m${i}v`]} onChange={(e) => setCsForm({ ...csForm, [`m${i}v`]: e.target.value })} />
                  <input className={inputCls} placeholder="Conversion" value={csForm[`m${i}l`]} onChange={(e) => setCsForm({ ...csForm, [`m${i}l`]: e.target.value })} />
                </div>
              ))}
            </div>
            <div className="mt-6 space-y-5">
              <div><label className={labelCls}>Challenge</label><textarea rows={3} className={inputCls + " resize-none"} value={csForm.challenge} onChange={(e) => setCsForm({ ...csForm, challenge: e.target.value })} /></div>
              <div><label className={labelCls}>Solution</label><textarea rows={3} className={inputCls + " resize-none"} value={csForm.solution} onChange={(e) => setCsForm({ ...csForm, solution: e.target.value })} /></div>
              <div><label className={labelCls}>Results</label><textarea rows={3} className={inputCls + " resize-none"} value={csForm.results} onChange={(e) => setCsForm({ ...csForm, results: e.target.value })} /></div>
              <div><label className={labelCls}>Gallery URLs (one per line)</label><textarea rows={3} className={inputCls + " resize-none"} value={csForm.gallery} onChange={(e) => setCsForm({ ...csForm, gallery: e.target.value })} /></div>
            </div>
            <label className="flex items-center gap-3 mt-6 cursor-pointer font-mono text-sm text-white/70" data-testid="cs-featured">
              <input type="checkbox" checked={csForm.featured !== false} onChange={(e) => setCsForm({ ...csForm, featured: e.target.checked })} className="w-4 h-4 accent-[color:var(--accent)]" />
              Show on homepage
            </label>
            <div className="flex gap-3 mt-8">
              <button type="submit" className="bg-white text-black px-8 py-4 font-mono text-xs tracking-[0.2em] uppercase hover:bg-[color:var(--accent)] hover:text-white transition-colors" data-testid="cs-save">Save</button>
              <button type="button" onClick={() => setCsForm(null)} className="border border-white/20 px-8 py-4 font-mono text-xs tracking-[0.2em] uppercase hover:bg-white hover:text-black transition-colors">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Testimonial drawer */}
      {tForm && (
        <div className="fixed inset-0 z-[130] flex justify-end" data-testid="t-form">
          <div className="absolute inset-0 bg-black/70" onClick={() => setTForm(null)} />
          <form onSubmit={saveT} className="relative w-full max-w-xl h-full bg-[color:var(--surface)] border-l border-white/10 overflow-y-auto p-8 md:p-10">
            <div className="flex items-center justify-between mb-8">
              <h3 className="font-display text-3xl uppercase tracking-tight">{tEditId ? "Edit" : "New"} testimonial</h3>
              <button type="button" onClick={() => setTForm(null)} className="p-2 hover:text-[color:var(--accent)]"><X size={20} /></button>
            </div>
            <div className="space-y-6">
              <div><label className={labelCls}>Quote *</label><textarea rows={4} className={inputCls + " resize-none"} value={tForm.quote} onChange={(e) => setTForm({ ...tForm, quote: e.target.value })} data-testid="t-quote" /></div>
              <div><label className={labelCls}>Name *</label><input className={inputCls} value={tForm.name} onChange={(e) => setTForm({ ...tForm, name: e.target.value })} data-testid="t-name" /></div>
              <div><label className={labelCls}>Role</label><input className={inputCls} value={tForm.role} onChange={(e) => setTForm({ ...tForm, role: e.target.value })} placeholder="CEO, Company" /></div>
            </div>
            <div className="flex gap-3 mt-8">
              <button type="submit" className="bg-white text-black px-8 py-4 font-mono text-xs tracking-[0.2em] uppercase hover:bg-[color:var(--accent)] hover:text-white transition-colors" data-testid="t-save">Save</button>
              <button type="button" onClick={() => setTForm(null)} className="border border-white/20 px-8 py-4 font-mono text-xs tracking-[0.2em] uppercase hover:bg-white hover:text-black transition-colors">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Blog drawer */}
      {blogForm && (
        <div className="fixed inset-0 z-[130] flex justify-end" data-testid="blog-form">
          <div className="absolute inset-0 bg-black/70" onClick={() => setBlogForm(null)} />
          <form onSubmit={saveBlog} className="relative w-full max-w-2xl h-full bg-[color:var(--surface)] border-l border-white/10 overflow-y-auto p-8 md:p-10">
            <div className="flex items-center justify-between mb-8">
              <h3 className="font-display text-3xl uppercase tracking-tight">{blogEditId ? "Edit" : "New"} post</h3>
              <button type="button" onClick={() => setBlogForm(null)} className="p-2 hover:text-[color:var(--accent)]"><X size={20} /></button>
            </div>
            <div className="grid grid-cols-2 gap-5">
              <div><label className={labelCls}>Slug *</label><input className={inputCls} value={blogForm.slug} onChange={(e) => setBlogForm({ ...blogForm, slug: e.target.value })} placeholder="my-post" data-testid="blog-slug" /></div>
              <div><label className={labelCls}>Category</label><input className={inputCls} value={blogForm.category} onChange={(e) => setBlogForm({ ...blogForm, category: e.target.value })} placeholder="Gen AI" /></div>
              <div className="col-span-2"><label className={labelCls}>Title *</label><input className={inputCls} value={blogForm.title} onChange={(e) => setBlogForm({ ...blogForm, title: e.target.value })} data-testid="blog-title" /></div>
              <div className="col-span-2"><label className={labelCls}>Excerpt</label><textarea rows={2} className={inputCls + " resize-none"} value={blogForm.excerpt} onChange={(e) => setBlogForm({ ...blogForm, excerpt: e.target.value })} /></div>
              <div><label className={labelCls}>Author</label><input className={inputCls} value={blogForm.author} onChange={(e) => setBlogForm({ ...blogForm, author: e.target.value })} /></div>
              <div><label className={labelCls}>Read time</label><input className={inputCls} value={blogForm.read} onChange={(e) => setBlogForm({ ...blogForm, read: e.target.value })} placeholder="5 min" /></div>
              <div><label className={labelCls}>Date (YYYY-MM-DD)</label><input className={inputCls} value={blogForm.date} onChange={(e) => setBlogForm({ ...blogForm, date: e.target.value })} placeholder="auto if blank" /></div>
              <div><label className={labelCls}>Cover image URL</label><input className={inputCls} value={blogForm.cover} onChange={(e) => setBlogForm({ ...blogForm, cover: e.target.value })} /></div>
              <div className="col-span-2"><label className={labelCls}>Body</label><RichTextEditor value={blogForm.body} onChange={(html) => setBlogForm({ ...blogForm, body: html })} testid="blog-body" /></div>
            </div>
            <div className="flex gap-3 mt-8">
              <button type="submit" className="bg-white text-black px-8 py-4 font-mono text-xs tracking-[0.2em] uppercase hover:bg-[color:var(--accent)] hover:text-white transition-colors" data-testid="blog-save">Publish</button>
              <button type="button" onClick={() => setBlogForm(null)} className="border border-white/20 px-8 py-4 font-mono text-xs tracking-[0.2em] uppercase hover:bg-white hover:text-black transition-colors">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
